import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Container,
  Avatar,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
   Fade,
  Slide,
  Zoom,
  Grow
} from '@mui/material';
import { Edit, Save, LockReset, PhotoCamera,CheckCircle  } from '@mui/icons-material';
import api from '../services/api';
import { keyframes } from '@emotion/react';

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0% { transform: scale(0.95); }
  50% { transform: scale(1.05); }
  100% { transform: scale(0.95); }
`;


const Profile = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    avatar: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await api.get(`/users/${user._id}`);
        setFormData({
          name: response.data.data.name,
          email: response.data.data.email,
          phone: response.data.phone || '',
          avatar: response.data.avatar || ''
        });
      } catch (err) {
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user._id]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const response = await api.patch('/users/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, avatar: response.data.avatar }));
      updateUser(response.data);
      setSuccess('Avatar updated successfully');
    } catch (err) {
      setError('Failed to update avatar');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await api.put(`/users/${user._id}`, formData);
      updateUser(response.data);
      setIsEditing(false);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, minHeight: '100vh' }}>
      <Grow in={true} timeout={800}>
        <Paper elevation={6} sx={{ 
          p: 4,
          borderRadius: 4,
          background: 'linear-gradient(-45deg, #f3f4f6, #ffffff, #f8f9fa)',
          backgroundSize: '400% 400%',
          animation: `${gradientAnimation} 10s ease infinite`,
          position: 'relative',
          overflow: 'hidden',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            background: 'linear-gradient(45deg, #2196f3, #e91e63)',
            borderRadius: '50%',
            filter: 'blur(80px)',
            opacity: 0.15,
          }
        }}>
          <Box sx={{ textAlign: 'center', mb: 4, position: 'relative' }}>
            <input
              accept="image/*"
              id="avatar-upload"
              type="file"
              hidden
              onChange={handleAvatarUpload}
            />
            <label htmlFor="avatar-upload">
              <Box sx={{
                position: 'relative',
                display: 'inline-block',
                '&:hover .avatar-overlay': {
                  opacity: 1
                }
              }}>
                <Avatar
                  src={formData.avatar || `https://ui-avatars.com/api/?name=${formData.name}`}
                  sx={{ 
                    width: 140,
                    height: 140,
                    margin: '0 auto 16px',
                    cursor: 'pointer',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    },
                    border: '3px solid',
                    borderColor: 'primary.main',
                    boxShadow: 6
                  }}
                />
                <Box
                  className="avatar-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s'
                  }}
                >
                  <PhotoCamera sx={{ color: 'white', fontSize: 40 }} />
                </Box>
              </Box>
            </label>
            
            <Zoom in={!!success} style={{ transitionDelay: success ? '200ms' : '0ms' }}>
              <CheckCircle 
                color="success" 
                sx={{ 
                  position: 'absolute', 
                  top: 10, 
                  right: 10,
                  animation: `${pulse} 2s infinite`
                }} 
              />
            </Zoom>

            <Slide in direction="down" timeout={500}>
              <Typography variant="h3" component="h1" gutterBottom sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #2196f3 30%, #e91e63 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {formData.name}
              </Typography>
            </Slide>
            
            <Fade in timeout={1000}>
              <Typography variant="subtitle1" color="text.secondary" sx={{ 
                letterSpacing: 2,
                textTransform: 'uppercase',
                mt: -1
              }}>
                {user.role}
              </Typography>
            </Fade>
          </Box>

          {error && (
            <Slide in direction="right" timeout={300}>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                {error}
              </Alert>
            </Slide>
          )}
          
          {success && (
            <Slide in direction="left" timeout={300}>
              <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
                {success}
              </Alert>
            </Slide>
          )}

          <Divider sx={{ 
            mb: 4,
            bgcolor: 'primary.main',
            height: 2,
            borderRadius: 2
          }} />

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {['name', 'email', 'phone'].map((field, index) => (
                <Grow 
                  key={field}
                  in={true}
                  timeout={index * 200 + 500}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={field.charAt(0).toUpperCase() + field.slice(1)}
                      name={field}
                      value={formData[field]}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      required={field !== 'phone'}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          transition: 'all 0.3s',
                          '&.Mui-focused': {
                            boxShadow: '0 0 0 2px rgba(33,150,243,0.2)'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grow>
              ))}

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 2 }}>
                {isEditing ? (
                  <>
                    <Zoom in={isEditing}>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<Save />}
                        sx={{
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                          textTransform: 'none',
                          fontSize: 16,
                          transition: 'all 0.3s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 4
                          }
                        }}
                      >
                        Save Changes
                      </Button>
                    </Zoom>
                    <Zoom in={isEditing}>
                      <Button
                        variant="outlined"
                        onClick={() => setIsEditing(false)}
                        sx={{
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                          textTransform: 'none',
                          fontSize: 16
                        }}
                      >
                        Cancel
                      </Button>
                    </Zoom>
                  </>
                ) : (
                  <>
                    <Zoom in={!isEditing}>
                      <Button
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={() => setIsEditing(true)}
                        sx={{
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                          textTransform: 'none',
                          fontSize: 16,
                          background: 'linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 4
                          }
                        }}
                      >
                        Edit Profile
                      </Button>
                    </Zoom>
                    <Zoom in={!isEditing} style={{ transitionDelay: '100ms' }}>
                      <Button
                        variant="outlined"
                        startIcon={<LockReset />}
                        href="/change-password"
                        sx={{
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                          textTransform: 'none',
                          fontSize: 16,
                          borderWidth: 2,
                          '&:hover': {
                            borderWidth: 2
                          }
                        }}
                      >
                        Change Password
                      </Button>
                    </Zoom>
                  </>
                )}
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grow>
    </Container>
  );
};

export default Profile;